import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton, {
  OutlineCustomButton,
} from "shared/components/inputButton";
import { GetContractDetailsActon } from "store/actions/ContractActions";
import SignatureModal from "../DigitalSign";
import { formatDateToMMDDYYYY } from "utils/common";

const PublishingSplitDocument = ({ contractDetails, sign, setSign, onSignSubmit, handleClear }) => {
  const date = formatDateToMMDDYYYY(contractDetails?.payload?.agreementDate);

  const includeArtist =
    contractDetails?.payload?.details?.publishingSplit === "Yes";

  const InformationTemplate = () => {
    // Calculate shares based on artist inclusion
    const artistShare = includeArtist ? "33.33%" : "0%";
    const coWriterShare = includeArtist ? "33.33%" : "50%";
    const totalShare = includeArtist ? "100%" : "100%";

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
        }}
      >
        {includeArtist && (
          <>
            <div style={{ marginBottom: "0.5rem" }}>
              <p style={{ margin: "0.25rem 0", fontWeight: "600" }}>Artist</p>
              <p style={{ margin: "0.25rem 0" }}>Publisher Name</p>
              <p style={{ margin: "0.25rem 0" }}>
                Publishing Identification Number
              </p>
              <p style={{ margin: "0.25rem 0" }}>Address of the Publisher</p>
            </div>
            <hr style={{ margin: "0.5rem 0", borderTop: "1px dashed #ccc" }} />
          </>
        )}

        <div style={{ marginBottom: "0.5rem" }}>
          <p style={{ margin: "0.25rem 0", fontWeight: "600" }}>Co-writer #1</p>
          <p style={{ margin: "0.25rem 0" }}>Publisher Name</p>
          <p style={{ margin: "0.25rem 0" }}>
            Publishing Identification Number
          </p>
          <p style={{ margin: "0.25rem 0" }}>Address of the Publisher</p>
        </div>

        <hr style={{ margin: "0.5rem 0", borderTop: "1px dashed #ccc" }} />

        <div style={{ marginBottom: "0.5rem" }}>
          <p style={{ margin: "0.25rem 0", fontWeight: "600" }}>Co-writer #2</p>
          <p style={{ margin: "0.25rem 0" }}>Publisher Name</p>
          <p style={{ margin: "0.25rem 0" }}>
            Publishing Identification Number
          </p>
          <p style={{ margin: "0.25rem 0" }}>Address of the Publisher</p>
        </div>
      </div>
    );
  };

  const CompositionTable = [
    {
      title: "Forestry",
      information: <InformationTemplate />, 
      publish: "100%", 
    },
  ];

  const publishShareBodyTemplate = () => {
    const containerStyle = {
      display: "flex",
      flexDirection: "column",
      gap: "0.5rem",
      height: "100%",
      padding: "0.5rem 0" 
    };
  
    const shareItemStyle = {
      padding: "0.25rem 0",
      textAlign: "center"
    };
  
    const dividerStyle = {
      margin: "0.5rem 0",
      borderTop: "1px dashed #ccc", 
      width: "100%"
    };
  
    if (includeArtist) {
      return (
        <div style={containerStyle}>
          <div style={shareItemStyle}>33.33%</div>
          <hr style={dividerStyle} />
          <div style={shareItemStyle}>33.33%</div>
          <hr style={dividerStyle} />
          <div style={shareItemStyle}>33.33%</div>
        </div>
      );
    } else {
      return (
        <div style={containerStyle}>
          <div style={shareItemStyle}>50%</div>
          <hr style={dividerStyle} />
          <div style={shareItemStyle}>50%</div>
        </div>
      );
    }
  };

  return (
    <div>
      <div className="bg-white shadow-1 p-5 ">
        <h1 className="mb-5 text-center">
          {" "}
          <strong>
            {contractDetails?.payload?.agreementType || "____________"}
          </strong>
        </h1>
        <p className="mb-3">
          This publishing split form (the{" "}
          <strong>{contractDetails?.payload?.agreementName}</strong>), is
          entered into and made effective as of of{" "}
          <strong>{date || "____________"}</strong>..
        </p>
        <p className="mb-3">By and Between </p>
        <p className="mb-3">
          <strong>
            {contractDetails?.payload?.primaryArtist?.fullLegalName ||
              "____________"}
          </strong>
          , p/k/a{" "}
          <strong>
            {contractDetails?.payload?.primaryArtist?.name || "____________"}
          </strong>{" "}
          (the “Artist”) with an address at{" "}
          <strong>
            {contractDetails?.payload?.primaryArtist?.address || "____________"}
          </strong>
        </p>
        <p className="mb-3">And</p>
        <p className="mb-3">
          <strong>
            {contractDetails?.payload?.details?.coWriterFullLegalName ||
              "____________"}
          </strong>
          , p/k/a{" "}
          <strong>
            {contractDetails?.payload?.details?.coWriterName || "____________"}{" "}
          </strong>{" "}
          (the <strong>Co-writer #1 </strong>) with an address at{" "}
          <strong>
            {contractDetails?.payload?.details?.coWriterAddress ||
              "____________"}
          </strong>
        </p>
        <p className="mb-3">
          <strong>
            {contractDetails?.payload?.details?.coWriterFullLegalName2 ||
              "____________"}
          </strong>
          , p/k/a{" "}
          <strong>
            {contractDetails?.payload?.details?.coWriterName2 || "____________"}{" "}
          </strong>{" "}
          (the <strong>Co-writer #2</strong>) with an address at{" "}
          <strong>
            {contractDetails?.payload?.details?.coWriterAddress2 ||
              "____________"}{" "}
          </strong>
        </p>
        <p className="mb-3">Collectively referred to as the “Co-writers”.</p>
        <h3 className="mt-5 mb-3">1. PUBLISHING SPLITS</h3>
        <p className="mb-3">
          The following shall confirm the writer/publisher splits with respect
          to the Composition listed below and performed by the Artist.
        </p>
        <p className="mb-3">Table 1 - Composition Split</p>
        <DataTable
          value={CompositionTable}
          showGridlines
          tableStyle={{ minWidth: "50rem" }}
        >
          <Column field="title" header="Composition Title"></Column>
          <Column
            field="information"
            header="Writer / Publisher Information"
            body={(rowData) => rowData.information}
          ></Column>
          <Column
            header="Share - Publishing"
            body={publishShareBodyTemplate}
            style={{ padding: 0 }}
          ></Column>
        </DataTable>
        <h3 className="mt-5 mb-3">
          2. Grant of Controlled Composition License and Mechanical Royalty
          Limitations
        </h3>
        <p className="mb-3">
          The Co-Writers hereby agree, and shall ensure that your publishing
          designee agrees, to license your shares, if any, in and to the
          Compositions to the Artist and their respective designees and
          assignees (collectively, the “Licensees”), as if such shares
          constituted a “Controlled Composition.” Such licensing shall be
          subject to a mechanical royalty at a rate equal to a maximum of [
          <strong>
            {contractDetails?.payload?.details?.controlledComposition ||
              "____________"}{" "}
          </strong>
          ].
        </p>
        <h3 className="mt-5 mb-3">3. Compliance with Statutory Requirements</h3>
        <p className="mb-3">
          The Licensees shall ensure that all payments of mechanical royalties
          are made in compliance with applicable copyright laws and regulations,
          subject to the rates agreed under this Agreement.
        </p>
        <h3 className="mt-5 mb-3">4. Notifications and Approvals</h3>
        <p className="mb-3">
          The Co-writers shall be notified in writing of any uses of the
          Compositions and corresponding mechanical royalties earned therefrom,
          and the Co-writers shall have the right to review and approve any
          accounting statements related to the licensing of the Compositions,
          provided that such approval shall not be unreasonably withheld.
        </p>

        <p className="mt-7 mb-3">ACCEPTED AND AGREED TO BY:</p>
        {/* Artist Section */}
        <p className="mt-6 mb-3">Artist</p>
        <h3 className="mt-6 mb-3">
          <div style={{ position: "relative" }}>
            {contractDetails?.payload?.signatures?.find(
              (sig) => sig.role === "artist"
            ) ? (
              contractDetails.payload.signatures.find(
                (sig) => sig.role === "artist"
              ).signature ? (
                <img
                  src={
                    contractDetails.payload.signatures.find(
                      (sig) => sig.role === "artist"
                    ).signature
                  }
                  style={{ width: "150px", height: "auto" }}
                  alt="Artist Signature"
                />
              ) : (
                <div
                  style={{
                    minWidth: "150px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "4px",
                    fontFamily: "cursive",
                  }}
                >
                  {contractDetails.payload.signatures.find(
                    (sig) => sig.role === "artist"
                  ).typedSignature || " "}
                </div>
              )
            ) : sign.artist?.signature ? (
              <img
                src={sign.artist.signature}
                style={{ width: "150px", height: "auto" }}
                alt="Artist Signature"
              />
            ) : (
              <div
                style={{
                  minWidth: "150px",
                  borderBottom: "1px solid #000",
                  paddingBottom: "4px",
                  fontFamily: "cursive",
                }}
              >
                {sign.artist?.typedSignature || " "}
              </div>
            )}

            {!contractDetails?.payload?.signatures?.find(
              (sig) => sig.role === "artist"
            ) && (
              <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                {!sign.artist?.signature && !sign.artist?.typedSignature ? (
                  <CustomButton
                    label="Sign"
                    onClick={() => onSignSubmit("artist")}
                    disabled={
                      !!sign.coWriter?.signature ||
                      !!sign.coWriter?.typedSignature ||
                      !!sign.coWriter2?.signature ||
                      !!sign.coWriter2?.typedSignature
                    }
                  />
                ) : (
                  <>
                    <CustomButton
                      label="Resign"
                      onClick={() => onSignSubmit("artist")}
                      disabled={
                        !!sign.coWriter?.signature ||
                        !!sign.coWriter?.typedSignature ||
                        !!sign.coWriter2?.signature ||
                        !!sign.coWriter2?.typedSignature
                      }
                    />
                    <OutlineCustomButton
                      label="Clear"
                      onClick={() => {
                        if (typeof handleClear === "function") {
                          handleClear("artist");
                        } else {
                          setSign((prev) => ({
                            ...prev,
                            artist: {
                              signature: "",
                              typedSignature: "",
                            },
                          }));
                        }
                      }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </h3>

        {/* Co-Writer Section */}
        <p className="mt-6 mb-3">Co-Writer</p>
        <h3 className="mt-6 mb-3">
          <div style={{ position: "relative" }}>
            {contractDetails?.payload?.signatures?.find(
              (sig) => sig.role === "coWriter"
            ) ? (
              contractDetails.payload.signatures.find(
                (sig) => sig.role === "coWriter"
              ).signature ? (
                <img
                  src={
                    contractDetails.payload.signatures.find(
                      (sig) => sig.role === "coWriter"
                    ).signature
                  }
                  style={{ width: "150px", height: "auto" }}
                  alt="Co-Writer Signature"
                />
              ) : (
                <div
                  style={{
                    minWidth: "150px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "4px",
                    fontFamily: "cursive",
                  }}
                >
                  {contractDetails.payload.signatures.find(
                    (sig) => sig.role === "coWriter"
                  ).typedSignature || " "}
                </div>
              )
            ) : sign.coWriter?.signature ? (
              <img
                src={sign.coWriter.signature}
                style={{ width: "150px", height: "auto" }}
                alt="Co-Writer Signature"
              />
            ) : (
              <div
                style={{
                  minWidth: "150px",
                  borderBottom: "1px solid #000",
                  paddingBottom: "4px",
                  fontFamily: "cursive",
                }}
              >
                {sign.coWriter?.typedSignature || " "}
              </div>
            )}

            {!contractDetails?.payload?.signatures?.find(
              (sig) => sig.role === "coWriter"
            ) && (
              <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                {!sign.coWriter?.signature && !sign.coWriter?.typedSignature ? (
                  <CustomButton
                    label="Sign"
                    onClick={() => onSignSubmit("coWriter")}
                    disabled={
                      !!sign.artist?.signature ||
                      !!sign.artist?.typedSignature ||
                      !!sign.coWriter2?.signature ||
                      !!sign.coWriter2?.typedSignature
                    }
                  />
                ) : (
                  <>
                    <CustomButton
                      label="Resign"
                      onClick={() => onSignSubmit("coWriter")}
                      disabled={
                        !!sign.artist?.signature ||
                        !!sign.artist?.typedSignature ||
                        !!sign.coWriter2?.signature ||
                        !!sign.coWriter2?.typedSignature
                      }
                    />
                    <OutlineCustomButton
                      label="Clear"
                      onClick={() => {
                        if (typeof handleClear === "function") {
                          handleClear("coWriter");
                        } else {
                          setSign((prev) => ({
                            ...prev,
                            coWriter: {
                              signature: "",
                              typedSignature: "",
                            },
                          }));
                        }
                      }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </h3>

        {/* Co-Writer 2 Section */}
        <p className="mt-6 mb-3">Co-Writer 2</p>
        <h3 className="mt-6 mb-3">
          <div style={{ position: "relative" }}>
            {contractDetails?.payload?.signatures?.find(
              (sig) => sig.role === "coWriter2"
            ) ? (
              contractDetails.payload.signatures.find(
                (sig) => sig.role === "coWriter2"
              ).signature ? (
                <img
                  src={
                    contractDetails.payload.signatures.find(
                      (sig) => sig.role === "coWriter2"
                    ).signature
                  }
                  style={{ width: "150px", height: "auto" }}
                  alt="Co-Writer 2 Signature"
                />
              ) : (
                <div
                  style={{
                    minWidth: "150px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "4px",
                    fontFamily: "cursive",
                  }}
                >
                  {contractDetails.payload.signatures.find(
                    (sig) => sig.role === "coWriter2"
                  ).typedSignature || " "}
                </div>
              )
            ) : sign.coWriter2?.signature ? (
              <img
                src={sign.coWriter2.signature}
                style={{ width: "150px", height: "auto" }}
                alt="Co-Writer 2 Signature"
              />
            ) : (
              <div
                style={{
                  minWidth: "150px",
                  borderBottom: "1px solid #000",
                  paddingBottom: "4px",
                  fontFamily: "cursive",
                }}
              >
                {sign.coWriter2?.typedSignature || " "}
              </div>
            )}

            {!contractDetails?.payload?.signatures?.find(
              (sig) => sig.role === "coWriter2"
            ) && (
              <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                {!sign.coWriter2?.signature &&
                !sign.coWriter2?.typedSignature ? (
                  <CustomButton
                    label="Sign"
                    onClick={() => onSignSubmit("coWriter2")}
                    disabled={
                      !!sign.artist?.signature ||
                      !!sign.artist?.typedSignature ||
                      !!sign.coWriter?.signature ||
                      !!sign.coWriter?.typedSignature
                    }
                  />
                ) : (
                  <>
                    <CustomButton
                      label="Resign"
                      onClick={() => onSignSubmit("coWriter2")}
                      disabled={
                        !!sign.artist?.signature ||
                        !!sign.artist?.typedSignature ||
                        !!sign.coWriter?.signature ||
                        !!sign.coWriter?.typedSignature
                      }
                    />
                    <OutlineCustomButton
                      label="Clear"
                      onClick={() => {
                        if (typeof handleClear === "function") {
                          handleClear("coWriter2");
                        } else {
                          setSign((prev) => ({
                            ...prev,
                            coWriter2: {
                              signature: "",
                              typedSignature: "",
                            },
                          }));
                        }
                      }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </h3>
      </div>
    </div>
  );
};

export default PublishingSplitDocument;
